import type { WomenTripBookingDTO } from "@majourneys/shared";
import type { TransferRecord } from "@majourneys/shared";

// NOTE: Eden treaty cannot resolve api.bookings({ id }).get() cleanly because
// two Elysia plugins share the /bookings prefix — bookingsRoutes (POST /:bookingId/cancellations)
// and bookingDetailsRoutes (GET /:id). The resulting union type requires both { id } and
// { bookingId }, so we fall back to a typed fetch stub here.

export interface BookingDetail {
  id: string;
  status: "PENDING" | "CONFIRMED" | "CANCELLED" | "REJECTED" | "PAID" | "COMPLETED";
  reference: string;
  createdAt: string;
  activity: {
    id: string;
    slug: string;
    title: string;
    image: string;
    /** Who was playing the booked night, for a venue with a lineup. */
    lineup?: string | null;
    cityName: string;
    /** City slug (`marrakech`, `agafay`), whatever the page language. */
    citySlug?: string | null;
    overview: string;
    /** Activity coordinates for the thank-you map (null when unset). */
    latitude?: string | null;
    longitude?: string | null;
    /** When true, the exact location is hidden and no map is shown. */
    hideLocation?: boolean;
    /** Provider takes cash only on arrival (no card machine). */
    cashOnlyOnArrival?: boolean;
    /** Category slug — lets the client name the per-group unit ("quad"). */
    categorySlug?: string | null;
    /** A restaurant table: held, not sold, so no money is shown for it. */
    tableBooking?: boolean;
    /** Booking template (`transfer`, `restaurant`, `experience`, ...). */
    template?: string | null;
  };
  package: {
    id: string;
    name: string;
    description: string;
    /** Seats on the booked package; names the vehicle on a transfer. */
    maxGuests?: number | null;
  };
  /** A transfer's pick-up, optional second place and times; null otherwise. */
  transfer?: TransferRecord | null;
  /**
   * Every treatment on the booking. A spa booking holds several with their own
   * quantities; everywhere else it is the single package above with quantity 1.
   */
  packs?: Array<{
    id: string | null;
    name: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
  }>;
  reservation: {
    date: string;
    /** "HH:mm". For a flexible package, the start of its window. */
    time?: string;
    /** End of a flexible package's window ("15:00"), shown as "13:00–15:00". */
    timeEnd?: string | null;
    adults: number;
    children: number;
    infants: number;
    hours?: number;
    nights?: number;
    groups?: number;
    transport: boolean;
  };
  pricing: {
    typePrice: 0 | 1 | 2 | 3;
    adult: number;
    child: number;
    infant: number;
    transportPrice: number;
    discount: number;
    total: number;
  };
  /** Add-on extras chosen at checkout (e.g. camel ride, buggy). */
  addons: Array<{
    variantId: string;
    addonName: string;
    label: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
  }>;
  /** Guest who placed the booking (drives post-booking create-account CTA). */
  guest: {
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  };
  /** True when the booking is completed and has not been reviewed yet. */
  canReview: boolean;
  /** True when a review already exists for this reservation. */
  alreadyReviewed: boolean;
  /**
   * Guest-side confirmation by email: `requested` once the team emailed the
   * guest to confirm, `confirmedAt` once they did. Optional so an older API
   * that does not send it reads as "never asked".
   */
  guestConfirmation?: { requested: boolean; confirmedAt: string | null };
  /**
   * Women-only trips: the deposit the guest sends the organiser, the rest on
   * the day, whether the team has seen it arrive, and where to send it.
   * Null (or absent, on an older API) for every other booking.
   */
  womenTrip?: WomenTripBookingDTO | null;
}

import { apiBase, apiHeaders } from "../lib/api-base";

type BookingApiError = {
  status: number;
  code: string;
  message: string;
  details?: unknown;
};

function apiBaseUrl(): string {
  return apiBase();
}

export interface ResendEmailResult {
  sent: boolean;
  to: string;
  reference: string | null;
}

async function readApiError(res: Response, fallbackMessage: string): Promise<BookingApiError> {
  const body = (await res.json().catch(() => null)) as
    | { error?: { code?: string; message?: string; details?: unknown } }
    | null;
  return {
    status: res.status,
    code: body?.error?.code ?? `http_${res.status}`,
    message: body?.error?.message ?? fallbackMessage,
    details: body?.error?.details,
  };
}

export const bookingsApi = {
  getById: async (id: string, lang?: string): Promise<BookingDetail> => {
    const url = new URL(`${apiBaseUrl()}/bookings/${id}`);
    if (lang) url.searchParams.set("lang", lang);
    const res = await fetch(url.toString(), { credentials: "include", headers: apiHeaders() });
    if (!res.ok) throw await readApiError(res, `Failed to fetch booking ${id}`);
    const json = (await res.json()) as { data: BookingDetail };
    return json.data;
  },
  /**
   * The guest's "yes, I'm coming" from the confirm-request email. Idempotent.
   * `auto` marks the call the page makes by itself on arrival from the email;
   * the API refuses it (code `tap_to_confirm`) in the first minutes after the
   * email went out, when the opener may be a mail scanner.
   */
  confirm: async (id: string, opts: { auto?: boolean } = {}): Promise<{ ok: true; confirmedAt: string }> => {
    const res = await fetch(`${apiBaseUrl()}/bookings/${id}/confirm`, {
      method: "POST",
      credentials: "include",
      headers: { ...apiHeaders(), "content-type": "application/json" },
      body: JSON.stringify({ auto: Boolean(opts.auto) }),
    });
    if (!res.ok) throw await readApiError(res, "Failed to confirm booking");
    const json = (await res.json()) as { data: { ok: true; confirmedAt: string } };
    return json.data;
  },
  cancel: async (id: string, reason?: string): Promise<{ ok: true; status: "CANCELLED" }> => {
    const res = await fetch(`${apiBaseUrl()}/bookings/${id}/cancel`, {
      method: "POST",
      credentials: "include",
      headers: { ...apiHeaders(), "content-type": "application/json" },
      body: JSON.stringify({ reason }),
    });
    const body = (await res.json().catch(() => null)) as
      | { data?: { ok: true; status: "CANCELLED" }; error?: { code?: string; message?: string; details?: unknown } }
      | null;
    if (!res.ok || !body?.data) {
      throw {
        status: res.status,
        code: body?.error?.code ?? `http_${res.status}`,
        message: body?.error?.message ?? "Failed to cancel booking",
        // Carries `reason` (date_locked, counts_locked, ...) for the guest's sentence.
        details: body?.error?.details,
      } satisfies BookingApiError;
    }
    return body.data;
  },
  modify: async (
    id: string,
    patch: {
      date?: string;
      time?: string;
      adults?: number;
      children?: number;
      infants?: number;
    },
  ): Promise<{ ok: true }> => {
    const res = await fetch(`${apiBaseUrl()}/bookings/${id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { ...apiHeaders(), "content-type": "application/json" },
      body: JSON.stringify(patch),
    });
    const body = (await res.json().catch(() => null)) as
      | { data?: { ok: true }; error?: { code?: string; message?: string; details?: unknown } }
      | null;
    if (!res.ok || !body?.data) {
      throw {
        status: res.status,
        code: body?.error?.code ?? `http_${res.status}`,
        message: body?.error?.message ?? "Failed to modify booking",
        // Carries `reason` (date_locked, counts_locked, ...) for the guest's sentence.
        details: body?.error?.details,
      } satisfies BookingApiError;
    }
    return body.data;
  },
  resendEmail: async (id: string): Promise<ResendEmailResult> => {
    const res = await fetch(`${apiBaseUrl()}/bookings/${id}/resend-email`, {
      method: "POST",
      credentials: "include",
      headers: apiHeaders(),
    });
    const body = (await res.json().catch(() => null)) as
      | { data?: ResendEmailResult; error?: { code?: string; message?: string; details?: unknown } }
      | null;
    if (!res.ok || !body?.data) {
      throw {
        status: res.status,
        code: body?.error?.code ?? `http_${res.status}`,
        message: body?.error?.message ?? "Failed to resend email",
        details: body?.error?.details,
      } satisfies BookingApiError;
    }
    return body.data;
  },
  /**
   * Public review submission — no auth required, the booking reference is the
   * authorization. Sends `multipart/form-data` (never set content-type manually —
   * the browser must generate the multipart boundary itself) so optional review
   * photos can ride along with the rating/body/display name. Throws a
   * BookingApiError carrying the API `code` so callers can distinguish
   * `already_reviewed` / `not_completed` / `invalid_photo` from generic failures.
   */
  submitReview: async (
    id: string,
    input: { rating: number; body: string; displayName?: string; images?: File[] },
  ): Promise<void> => {
    const form = new FormData();
    form.append("rating", String(input.rating));
    form.append("body", input.body);
    if (input.displayName?.trim()) form.append("display_name", input.displayName.trim());
    for (const image of input.images ?? []) form.append("images", image);

    const res = await fetch(`${apiBaseUrl()}/bookings/${id}/review`, {
      method: "POST",
      credentials: "include",
      headers: apiHeaders(),
      body: form,
    });
    if (!res.ok) throw await readApiError(res, "Failed to submit review");
  },
};

export type { BookingApiError };

export const bookingKeys = {
  all: ["bookings"] as const,
  detail: (id: string, lang?: string) =>
    [...bookingKeys.all, "detail", id, lang ?? "en"] as const,
};

/** React hook — returns the bookingsApi singleton bound to no specific context. */
export function useBookingsApi() {
  return bookingsApi;
}
